import { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Box, Theme, Popper, Button } from '@mui/material';
import {
  getOpenPanel,
  inspection,
  getIsSingleFile,
  getDocumentsState,
  getUnmatchedPages,
  getUnmatchedCropPages,
  getLastJobInput,
  getDocumentsLoaded,
  getHideDisplayPanels,
  getIsResultsPanelHidden,
  getFilesIds,
  getInspectionId,
  getSourceFileUnavailable,
  getTargetFileUnavailable,
} from 'store';
import { DocumentStates, DocumentTypes, CTFModuleTypes, InspectionStatuses } from 'types';
import { useQuery } from '@redux-requests/react';
import { fetchInspection } from 'store/request/inspections/actions';
import isReportsUrl from 'utils/location/isReportsUrl';
import { PagePreviewOffIcon, PagePreviewOnIcon } from 'components/icons';
import CloseIcon from '@mui/icons-material/Close';
import GVIconButton from 'components/lib/GVIconButton/GVIconButton';
import ThumbnailsPanel from 'components/pageElements/thumbnails/ThumbnailsPanel';
import { GVToggleButton } from 'components/common';
import { getStyleVariables, menuLightGrey, opacities } from 'styles/vars';
import GVTooltip from 'components/lib/GVToolTip/GVTooltip';
import RotatePageControl from './RotatePageControl';
import ZoomControls from './ZoomControls';
import PageSelection from './PageSelection';
import SyncScrollingButton from './SyncScrollingButton';
import CogMenu from './CogMenu';
import { makeStyles } from 'tss-react/mui';
import GVChip, { GVChipColor, GVChipSize } from 'components/GVChip/GVChip';
import { GVTextButton, GVTypography } from 'components/lib';
import { listToRange } from 'utils/pageRanges/pageRangeConversion';
import { colors } from 'components/lib/global/styles';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import ArrowLeftIcon from '@mui/icons-material/ArrowLeft';
import { isResultUrl } from 'utils/location';
import ReportDownloadMenu from '../GVDocumentsBar/ReportDownloadMenu';
import ShareIcon from 'components/icons/ShareInspection/ShareIcon';
import ShareModal from 'components/TopNav/ShareModal';
import { useTracker } from 'components/Tracker/TrackerProvider';
import { useCollaborators } from 'store/queries/collaborators/useCollaborators';

const useStyles = makeStyles()((theme: Theme) => {
  const styleVariables = getStyleVariables(theme);
  return {
    root: {
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: theme.palette.common.black,
      padding: theme.spacing(0.5, 0.5, 0.5, 0),
      borderLeft: 'none',
      height: theme.spacing(6),
      width: '100%',
      overflow: 'visible',
      position: 'relative',
      // Query container — descendants react to toolbar width (not viewport)
      // via @container, so panel collapse and snapped windows are handled.
      containerType: 'inline-size',
      containerName: 'gv-toolbar',
    },
    topBarContainer: {
      height: '100%',
    },
    itemsContainer: {
      marginRight: theme.spacing(2),
    },
    hidden: {
      display: 'none',
    },
    previewPopper: {
      ...styleVariables.leftPanel.previewPopper,
      width: '350px',
    },
    previewPopperSingleFile: {
      width: '200px',
      ...styleVariables.leftPanel.previewPopper,
    },
    graphicsPopper: {
      backgroundColor: menuLightGrey,
      borderRadius: '4px',
      width: '287px',
      color: '#FFFFFFDE',
      padding: '16px',
      marginTop: '4px',
    },
    graphicsPopperButtons: {
      flexDirection: 'row',
      justifyContent: 'flex-end',
      gap: theme.spacing(1),
    },
    backToPrepButton: {
      backgroundColor: 'transparent',
      border: '1px solid rgba(0, 0, 0, 0.23)',
      '&:hover': {
        backgroundColor: 'rgba(255, 255, 255, 0.04)',
        border: '1px solid rgba(0, 0, 0, 0.23)',
      },
    },
    closeIcon: {
      position: 'fixed',
      right: theme.spacing(1.5),
      top: theme.spacing(1.5),
      '&.MuiButtonBase-root': {
        borderRadius: 0,
      },
      '&:hover': {
        backgroundColor: 'rgb(255,255,255,0.24)',
      },
      zIndex: '100',
    },
    textButton: {
      color: theme.palette.common.white,
      opacity: opacities.high,
      textDecoration: 'underline',
      fontWeight: 700,
      fontStyle: 'italic',
    },
    previewButton: {
      margin: 0,
    },
    thumbnailContainer: {
      display: 'inline-flex',
      alignItems: 'center',
      width: 'auto',
      margin: `0 ${theme.spacing(2)}`,
    },
    buttonWrapper: {
      background: styleVariables.global.darkBorder,
      borderRadius: '4px',
      justifyContent: 'center',
      alignItems: 'center',
      flexBasis: 'fit-content',
      height: 'auto',
    },
    button: {
      margin: theme.spacing(0.25),
      color: theme.palette.text.primary,
      padding: `${theme.spacing(0.5)} ${theme.spacing(1.5)}`,
      background: 'transparent',
      fontWeight: 600,
      boxShadow: 'none',
      '&:hover': {
        background: styleVariables.colors.menuLightGrey,
        boxShadow: 'none',
      },
    },
    buttonActive: {
      background: theme.palette.secondary.main,
      '&:hover': {
        background: theme.palette.secondary.main,
        cursor: 'default',
      },
    },
    graphicsWarning: {
      position: 'fixed',
      zIndex: 1,
      width: '16px',
      height: '16px',
      fontWeight: 600,
      fontSize: '10px',
      marginLeft: '40px',
      marginTop: '-6px',
      '& .MuiChip-label': { overflow: 'visible' },
    },
    graphicsPopup: {
      zIndex: 1,
      width: '16px',
      height: '16px',
      fontWeight: 600,
      fontSize: '10px',
      padding: '16px',
    },
    tip: {
      fontWeight: 700,
    },
    bold: {
      fontWeight: 700,
      fontStyle: 'italic',
    },
    unmatchedList: {
      fontWeight: 700,
      color: colors.gvRed,
    },
    shareButton: {
      display: 'inline-flex',
      padding: '7px 10px 8px 10px',
      justifyContent: 'center',
      alignItems: 'center',
      gap: '6px',
      borderRadius: '4px',
      border: '1px solid var(--gds-color-cool-gray-900, #101113)',
      background: 'var(--gds-color-cool-gray-500, #292B30)',
      height: '32px',
      width: '32px',
      position: 'relative',
      boxSizing: 'border-box',
      '& svg': {
        width: '14px',
        height: '14px',
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
      },
      '&:hover': {
        display: 'inline-flex',
        padding: '7px 10px 8px 10px',
        justifyContent: 'center',
        alignItems: 'center',
        gap: '6px',
        borderRadius: '4px',
        border: '1px solid var(--gds-color-cool-gray-900, #101113)',
        background: 'var(--gds-color-cool-gray-450)',
      },
      '&:active': {
        display: 'inline-flex',
        padding: '7px 10px 8px 10px',
        justifyContent: 'center',
        alignItems: 'center',
        gap: '6px',
        borderRadius: '4px',
        border: '1px solid var(--primary-600-primary, #EA3632)',
        background: 'var(--primary-600-primary, #EA3632)',
      },
      '&.Mui-disabled': {
        display: 'inline-flex',
        padding: '7px 10px 8px 10px',
        justifyContent: 'center',
        alignItems: 'center',
        gap: '6px',
        borderRadius: '4px',
        border: '1px solid var(--gds-color-cool-gray-900, #101113)',
        background: 'var(--gds-color-cool-gray-700, #1F2126)',
        color: 'var(--surface-white-38, rgba(255, 255, 255, 0.38))',
        '& .MuiSvgIcon-root, & svg': {
          color: 'var(--surface-white-38, rgba(255, 255, 255, 0.38))',
        },
        '& .MuiButton-startIcon': {
          opacity: 0.38,
        },
      },
    },
    activeShareButton: {
      borderRadius: '4px',
      border: '1px solid var(--primary-600-primary, #EA3632)',
      background: 'var(--primary-600-primary, #EA3632)',
      '&:hover': {
        border: '1px solid var(--primary-600-primary, #EA3632)',
        background: 'var(--primary-600-primary, #EA3632)',
      },
    },
    contentContainer: {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      display: 'flex',
      alignItems: 'center',
      minWidth: '1110px',
      '--gds-spacing-field-container-margin': '0',
    },
    flexRow: {
      display: 'flex',
      alignItems: 'center',
      height: '100%',
      width: '100%',
    },
    toolbarGrid: {
      display: 'grid',
      gridTemplateColumns: 'auto minmax(0, 1fr) auto minmax(0, 1fr) auto',
      alignItems: 'center',
      width: '100%',
      height: '100%',
    },
    toolbarGridSingle: {
      display: 'grid',
      gridTemplateColumns: 'auto minmax(0, 1fr) auto',
      alignItems: 'center',
      width: '100%',
      height: '100%',
    },
    leftSection: {
      display: 'flex',
      alignItems: 'center',
      flexShrink: 0,
      minWidth: '130px',
      '@container gv-toolbar (min-width: 1500px)': {
        minWidth: '175px',
      },
    },
    rightSection: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-end',
      flexShrink: 0,
      gap: theme.spacing(1),
      paddingRight: theme.spacing(2),
      minWidth: '130px',
      '@container gv-toolbar (min-width: 1500px)': {
        minWidth: '175px',
      },
    },
    syncContainer: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
      height: '36px',
      padding: '0 16px',
    },
    docControlsHalf: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minWidth: 0,
      overflow: 'hidden',
      paddingLeft: theme.spacing(1),
      paddingRight: theme.spacing(1),
    },
    controlsContainer: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minWidth: '250px',
      overflow: 'hidden',
      zIndex: 1,
    },
    actionButtons: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1),
    },
    documentControls: {
      display: 'flex',
      alignItems: 'center',
      flexWrap: 'nowrap',
      overflow: 'hidden',
      gap: '8px',
      '& > *:last-child .MuiGrid-item': {
        marginRight: 0,
      },
    },
  };
});

const GVDocumentsTopBar = () => {
  const { classes, cx } = useStyles();
  const navigate = useNavigate();
  const anchorEl = useRef<HTMLDivElement | null>(null);
  const graphicsAnchorEl = useRef(null);
  const openPanel = useSelector(getOpenPanel);
  const isSingleFile = useSelector(getIsSingleFile);
  const documentsState = useSelector(getDocumentsState);
  const unmatchedPages = useSelector(getUnmatchedPages);
  const unmatchedCropPages = useSelector(getUnmatchedCropPages);
  const isDocumentsLoaded = useSelector(getDocumentsLoaded);
  const lastJobInput = useSelector(getLastJobInput);
  const isResultsPanelHidden = useSelector(getIsResultsPanelHidden);
  const inspectionId = useSelector(getInspectionId);
  const dispatch = useDispatch();
  const [showGraphicsPopup, setShowGraphicsPopup] = useState<boolean>(false);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const hideDisplay = useSelector(getHideDisplayPanels);
  const filesId = useSelector(getFilesIds);
  const sourceFileUnavailable = useSelector(getSourceFileUnavailable);
  const targetFileUnavailable = useSelector(getTargetFileUnavailable);
  const tracker = useTracker();
  const { collaborators, isShared, openToOrganization } = useCollaborators(inspectionId);
  const inspectionQuery = useQuery({ type: fetchInspection });
  const lastJob = inspectionQuery.data?.lastJob;
  const areFilesUnavailable =
    !!lastJob && (isSingleFile ? targetFileUnavailable : sourceFileUnavailable || targetFileUnavailable);
  const filesLoaded =
    areFilesUnavailable ||
    (isSingleFile && filesId.sampleFileId) ||
    (!isSingleFile && filesId.masterFileId && filesId.sampleFileId);

  const graphicsWarningMessage = useMemo(() => {
    if (unmatchedPages.length === 0 || !lastJobInput) {
      return null;
    }

    const masterInput = lastJobInput.documents[0];
    const includedPages = new Set(masterInput.pages.filter((page) => page.included).map((page) => page.number));

    const hasUnmatchedCrops = unmatchedCropPages.length > 0;
    const hasUnmatchedFullPages = unmatchedPages.length > unmatchedCropPages.length;

    // If there's a mix of unmatched crops and full pages, use generic message
    if (hasUnmatchedCrops && hasUnmatchedFullPages) {
      const unmatchedList = listToRange(unmatchedPages, 'p');
      return (
        <>
          <GVTypography fontSize={16} fontWeight={600} marginTop={'4px'} marginBottom={'16px'}>
            Graphics match incomplete
          </GVTypography>
          <GVTypography fontSize={12} fontWeight={400} marginBottom={'16px'}>
            We couldn't find matches for the following pages:{' '}
            <span className={classes.unmatchedList}>{unmatchedList}</span>. Try adjusting your cropped regions and
            checking your graphics settings.
          </GVTypography>
        </>
      );
    }

    if (hasUnmatchedCrops) {
      // If there are ONLY unmatched crop zones, show crop-specific message
      const unmatchedList = listToRange(unmatchedCropPages, 'p');
      return (
        <>
          <GVTypography fontSize={16} fontWeight={600} marginTop={'4px'} marginBottom={'16px'}>
            Graphics match incomplete
          </GVTypography>
          <GVTypography fontSize={12} fontWeight={400} marginBottom={'16px'}>
            We couldn't find matches for crop zones on {unmatchedCropPages.length === 1 ? 'page' : 'pages'}:{' '}
            <span className={classes.unmatchedList}>{unmatchedList}</span>. Try adjusting your cropped regions and
            checking your graphics settings.
          </GVTypography>
        </>
      );
    }

    if ([...includedPages].every((pageNumber) => unmatchedPages.includes(pageNumber))) {
      // if every included page failed (full page graphics only), we specify that every match failed
      return (
        <>
          <GVTypography fontSize={16} fontWeight={600} marginTop={'4px'} marginBottom={'16px'}>
            Graphics inspection failed
          </GVTypography>
          <GVTypography fontSize={12} fontWeight={400} marginBottom={'16px'}>
            We were unable to find graphics matches for any of your pages. Try adjusting your cropped regions and
            checking your graphics settings.
          </GVTypography>
        </>
      );
    }

    const unmatchedList = listToRange(unmatchedPages, 'p');
    return (
      <>
        <GVTypography fontSize={16} fontWeight={600} marginTop={'4px'} marginBottom={'16px'}>
          Graphics match incomplete
        </GVTypography>
        <GVTypography fontSize={12} fontWeight={400} marginBottom={'16px'}>
          We couldn't find matches for the following pages:{' '}
          <span className={classes.unmatchedList}>{unmatchedList}</span>. Try adjusting your cropped regions and
          checking your graphics settings.
        </GVTypography>
      </>
    );
  }, [unmatchedPages, unmatchedCropPages, lastJobInput]);

  useEffect(() => {
    // Only show graphics popup if the graphics job completed successfully but had unmatched pages
    const graphicsJob = lastJob?.ctfJobs?.find((job) => job.type === CTFModuleTypes.graphics);
    const graphicsJobCompleted = graphicsJob?.status === InspectionStatuses.completed;
    const hasUnmatchedPages = unmatchedPages.length > 0 && isDocumentsLoaded && graphicsJobCompleted;

    setShowGraphicsPopup(hasUnmatchedPages);
  }, [unmatchedPages, isDocumentsLoaded, lastJob]);

  const handleClick = () => {
    if (showGraphicsPopup && isResultUrl()) return;
    dispatch(inspection.actions.setOpenPanel(!openPanel));
  };

  const handleProofreaderClick = () => {
    const documentsLoading =
      documentsState.source === DocumentStates.LOADING || documentsState.target === DocumentStates.LOADING;
    if (!documentsLoading) {
      dispatch(inspection.actions.setSingleFileInspection());
    }
  };

  const bothDocumentsLoaded =
    (!isSingleFile &&
      documentsState.source === DocumentStates.LOADED &&
      documentsState.target === DocumentStates.LOADED) ||
    (isSingleFile && documentsState.target === DocumentStates.LOADED);

  // Center the toolbar whenever one document is shown: proofreader mode, or
  // comparison mode with a panel hidden by the view toggle.
  const isSinglePanelView = isSingleFile || hideDisplay.source || hideDisplay.target;
  const visiblePanelDocumentType = !isSingleFile && hideDisplay.target ? DocumentTypes.source : DocumentTypes.target;

  const renderDocumentControls = (documentType: DocumentTypes) => {
    return (
      documentsState[documentType] === DocumentStates.LOADED && (
        <div className={classes.documentControls}>
          <PageSelection documentType={documentType} />
          <ZoomControls document={documentType} />
          <RotatePageControl documentType={documentType} />
        </div>
      )
    );
  };

  const renderHideResultPanelButton = () => {
    if (!isResultUrl() || !isDocumentsLoaded) {
      // add placeholder spacing so the UI doesn't shift when navigating tabs
      return <div style={{ width: '24px', flexShrink: 0 }} />;
    }
    return (
      <Button
        variant="outlined"
        sx={{
          padding: '7px 0',
          minWidth: '2px',
          marginRight: '0',
          borderTopLeftRadius: 0,
          borderBottomLeftRadius: 0,
          backgroundColor: menuLightGrey,
          height: '32px',
          flexShrink: 0,
        }}
        id="collapse-results-button"
        onClick={() => {
          dispatch(inspection.actions.setIsResultsPanelHidden(!isResultsPanelHidden));
        }}
      >
        {isResultsPanelHidden ? <ArrowRightIcon fontSize="small" /> : <ArrowLeftIcon fontSize="small" />}
      </Button>
    );
  };

  const handleShareClick = () => {
    setShareModalOpen(true);
    tracker.track({
      name: 'inspection-share-modal-opened',
      data: {
        current_collaborator_count: collaborators?.length - 1 || 0, // -1 to exclude the owner
        open_to_organization: openToOrganization,
      },
    });
  };

  const handleCloseShareModal = () => {
    setShareModalOpen(false);
  };

  // TODO: Update with conditions
  const disableShareButton = false;

  const shareButton = (
    <GVTooltip title="Share Inspection" side="top">
      <GVIconButton
        size="small"
        onClick={handleShareClick}
        icon={<ShareIcon disabled={disableShareButton} />}
        id={'shareButton'}
        disabled={disableShareButton}
        className={cx(classes.shareButton, { [classes.activeShareButton]: shareModalOpen || isShared })}
      />
    </GVTooltip>
  );

  // Render thumbnail panel button
  const renderThumbnailButton = () => (
    <div className={classes.thumbnailContainer}>
      {unmatchedPages.length > 0 && (
        <GVChip
          label={unmatchedPages.length}
          className={classes.graphicsWarning}
          chipSize={GVChipSize.SMALL}
          chipColor={GVChipColor.ALERT}
          circle
        />
      )}
      <GVTooltip title="Thumbnail Panel" side="top" disableHoverableContent>
        <span style={{ display: 'inline-flex' }}>
          <GVToggleButton
            value="preview-panel"
            id="previewPanelToggleButton"
            small
            selected={openPanel}
            onChange={handleClick}
            className={classes.previewButton}
            ref={graphicsAnchorEl}
          >
            {openPanel ? <PagePreviewOnIcon fontSize="small" /> : <PagePreviewOffIcon fontSize="small" />}
          </GVToggleButton>
        </span>
      </GVTooltip>
    </div>
  );

  // Render action buttons (Report, Share, Settings)
  const renderActionButtons = () => (
    <div className={classes.actionButtons}>
      {isResultUrl() && (bothDocumentsLoaded || areFilesUnavailable) && (
        <>
          {bothDocumentsLoaded && <ReportDownloadMenu />}
          {shareButton}
        </>
      )}
      {!isReportsUrl() && bothDocumentsLoaded && <CogMenu loaded={bothDocumentsLoaded} />}
    </div>
  );

  // Render the select document mode buttons
  const renderDocumentModeButtons = () => (
    <div className={classes.buttonWrapper}>
      <Button
        variant="contained"
        className={cx(classes.button, { [classes.buttonActive]: !isSingleFile })}
        onClick={() => dispatch(inspection.actions.setInspectionSettings({ singleFile: false }))}
        startIcon={<img src="/icons/single-file/ic-comparison.svg" width={'16px'} height={'auto'} />}
        data-testid="comparison_button"
      >
        Comparison
      </Button>
      <Button
        variant="contained"
        className={cx(classes.button, { [classes.buttonActive]: isSingleFile })}
        onClick={handleProofreaderClick}
        startIcon={<img src="/icons/single-file/ic-single-doc.svg" width={'16px'} height={'auto'} />}
        data-testid="proofreader_button"
      >
        Proofreader
      </Button>
    </div>
  );

  return (
    <Box boxShadow={2} className={classes.root} ref={anchorEl}>
      <div className={classes.contentContainer}>
        {!filesLoaded ? (
          <div className={classes.flexRow} style={{ justifyContent: 'center' }}>
            {renderDocumentModeButtons()}
          </div>
        ) : isSinglePanelView ? (
          <div className={classes.toolbarGridSingle}>
            <div className={classes.leftSection}>
              {renderHideResultPanelButton()}
              {bothDocumentsLoaded && renderThumbnailButton()}
            </div>
            <div className={classes.docControlsHalf}>
              {bothDocumentsLoaded && renderDocumentControls(visiblePanelDocumentType)}
            </div>
            <div className={classes.rightSection}>{renderActionButtons()}</div>
          </div>
        ) : (
          <div className={classes.toolbarGrid}>
            <div className={classes.leftSection}>
              {renderHideResultPanelButton()}
              {bothDocumentsLoaded && renderThumbnailButton()}
            </div>
            <div className={classes.docControlsHalf}>
              {!hideDisplay.source && bothDocumentsLoaded && renderDocumentControls(DocumentTypes.source)}
            </div>
            <div className={classes.syncContainer}>
              {bothDocumentsLoaded && !hideDisplay.source && !hideDisplay.target && <SyncScrollingButton />}
            </div>
            <div className={classes.docControlsHalf}>
              {!hideDisplay.target && bothDocumentsLoaded && renderDocumentControls(DocumentTypes.target)}
            </div>
            <div className={classes.rightSection}>{renderActionButtons()}</div>
          </div>
        )}

        <Popper
          id="leftPanelPopper"
          open={(!showGraphicsPopup || !isResultUrl()) && openPanel}
          anchorEl={anchorEl ? anchorEl.current : null}
          placement="bottom-start"
          className={isSingleFile ? classes.previewPopperSingleFile : classes.previewPopper}
        >
          <ThumbnailsPanel />
        </Popper>

        {isResultUrl() && (
          <Popper
            id="close graphics popper"
            open={showGraphicsPopup}
            anchorEl={graphicsAnchorEl ? graphicsAnchorEl.current : null}
            placement="bottom-start"
            className={classes.graphicsPopper}
            modifiers={[
              {
                name: 'offset',
                options: {
                  offset: [-33, 8],
                },
              },
            ]}
          >
            <div>
              <GVIconButton
                className={classes.closeIcon}
                size="small"
                onClick={() => {
                  setShowGraphicsPopup(false);
                }}
                icon={<CloseIcon color="primary" />}
              />
            </div>
            {graphicsWarningMessage}
            <GVTypography fontSize={12} fontWeight={400} marginBottom={'16px'}>
              <span className={classes.bold}>Still having trouble? Check out our graphics prep tips & tricks </span>
              <a
                className={classes.textButton}
                href={`${import.meta.env.VITE_HELP_URL}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                here
              </a>
              <span className={classes.bold}>.</span>
            </GVTypography>
            <div className={classes.graphicsPopperButtons}>
              <GVTextButton
                className={classes.backToPrepButton}
                variant="outlined"
                text="Back to Prep"
                onClick={() => {
                  setShowGraphicsPopup(false);
                  navigate(`/inspection/${inspectionId}`);
                }}
              />
              <GVTextButton
                variant="contained"
                color="secondary"
                text="Close"
                onClick={() => {
                  setShowGraphicsPopup(false);
                }}
              />
            </div>
          </Popper>
        )}
      </div>
      <ShareModal open={shareModalOpen} onClose={handleCloseShareModal} />
    </Box>
  );
};

export default GVDocumentsTopBar;
